import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

/**
 * Public Terms of Service page (cream theme, Cormorant headlines).
 * Single source of truth - also linked from the consent checkbox on signup
 * and the discount-deal submission survey consent.
 */
const SECTIONS = [
  {
    n: 1,
    title: 'Acceptance of Terms',
    body: 'By creating an account or accessing the Mundalo platform, you agree to be bound by these Terms of Service. If you do not agree, do not use the platform.',
  },
  {
    n: 2,
    title: 'The Service',
    body: 'Mundalo provides relocation information, community features, AI-generated guidance, and related content for Americans relocating to Mexico. All content is informational only and does not constitute legal, tax, immigration, financial, or medical advice. Always consult a licensed professional for advice specific to your situation.',
  },
  {
    n: 3,
    title: 'AI-Generated Content',
    body: 'Mundalo uses artificial intelligence to generate personalized Blueprint documents. AI outputs are informational starting points, not professional advice. Mundalo makes no guarantees of accuracy, completeness, or fitness for any particular purpose.',
  },
  {
    n: 4,
    title: 'Eligibility',
    body: 'You must be at least 18 years old to use Mundalo. By registering, you confirm you meet this requirement.',
  },
  {
    n: 5,
    title: 'Account Responsibilities',
    body: 'You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate information and keep it current. You may not share your account with others.',
  },
  {
    n: 6,
    title: 'Payments & Refunds',
    body: 'All purchases are final. Mundalo does not offer refunds on one-time purchases (Blueprint Only, Essentials, Guided) once digital content has been accessed. Monthly and annual subscriptions may be cancelled at any time; cancellation takes effect at the end of the current billing period. Coaching call purchases are non-refundable if cancelled within 24 hours of the scheduled session.',
  },
  {
    n: 7,
    title: 'Community Standards',
    body: 'Members agree not to post content that is false, misleading, defamatory, harassing, discriminatory, or illegal. Mundalo reserves the right to remove content and suspend or terminate accounts that violate these standards without notice or refund.',
  },
  {
    n: 8,
    title: 'Housing & Listings',
    body: 'Member-submitted housing listings are provided as-is. Mundalo does not verify listing accuracy, inspect properties, or mediate disputes between listers and inquirers. Use your own judgment and conduct due diligence before any housing transaction.',
  },
  {
    n: 9,
    title: 'Local Deals & Partner Offers',
    body: 'Deals and partner offers are provided by third parties. Mundalo does not guarantee availability, accuracy, or quality of any third-party offer. Mundalo may receive compensation from partners.',
  },
  {
    n: 10,
    title: 'Intellectual Property',
    body: 'All Mundalo content, branding, city guides, course materials, and Blueprint documents are the intellectual property of Mundalo. You may not reproduce, distribute, or resell any Mundalo content without written permission.',
  },
  {
    n: 11,
    title: 'Privacy & Data',
    body: 'Mundalo collects and processes personal data as described in our Privacy Policy. By using the platform you consent to this processing. Mundalo does not sell your personal data to third parties.',
  },
  {
    n: 12,
    title: 'Email Communications',
    body: 'By creating an account you consent to receive transactional emails (account confirmations, Blueprint delivery, coaching reminders). Marketing and community digest emails require separate opt-in consent, which you may withdraw at any time from your profile settings.',
  },
  {
    n: 13,
    title: 'Limitation of Liability',
    body: "Mundalo's liability is limited to the amount you paid for the service in the 12 months preceding any claim. Mundalo is not liable for indirect, incidental, or consequential damages of any kind.",
  },
  {
    n: 14,
    title: 'Indemnification',
    body: 'You agree to indemnify and hold harmless Mundalo, its founders, employees, and contractors from any claims arising from your use of the platform or violation of these terms.',
  },
  {
    n: 15,
    title: 'Governing Law',
    body: 'These terms are governed by the laws of the State of Texas, United States. Any disputes shall be resolved in the courts of Travis County, Texas.',
  },
  {
    n: 16,
    title: 'Changes to Terms',
    body: 'Mundalo reserves the right to update these terms at any time. Continued use of the platform after changes constitutes acceptance.',
  },
];

const Terms = () => (
  <div className="min-h-screen bg-[#F9F6EF]" data-testid="terms-page">
    <SEO
      title="Terms of Service - Mundalo"
      description="Mundalo Terms of Service & Community Guidelines."
      path="/terms"
    />
    <Header currentPage="terms" />

    <section className="pt-32 pb-16">
      <div className="max-w-3xl mx-auto px-6">
        <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#1A5C6E] mb-2">
          Legal · Mundalo
        </p>
        <h1 className="font-display italic text-4xl sm:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
          Terms of Service &amp; Community Guidelines
        </h1>
        <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/55 mb-12">
          Effective Date: May 20, 2026
        </p>

        <div className="space-y-8" data-testid="terms-body">
          {SECTIONS.map((s) => (
            <section key={s.n} data-testid={`terms-section-${s.n}`}>
              <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-3">
                {s.n}. {s.title}
              </h2>
              <p className="font-body text-[15px] text-[#0A1F2E]/85 leading-relaxed">
                {s.body}
              </p>
            </section>
          ))}

          <section data-testid="terms-section-17">
            <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-3">
              17. Contact
            </h2>
            <p className="font-body text-[15px] text-[#0A1F2E]/85 leading-relaxed">
              Questions about these terms:{' '}
              <a
                href="mailto:hello@mundalo.com"
                className="text-[#1A5C6E] underline hover:text-[#0A1F2E]"
              >
                hello@mundalo.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default Terms;
